import { useEffect, useMemo, useState } from "react";

import { ServiceRequest } from "@medplum/fhirtypes";
import { useMedplum } from "@medplum/react";

import { Accordion, Group, Stack } from "@mantine/core";

import { IconTestPipe } from "@tabler/icons-react";

import { TextWrapper } from "@/components/TextWrapper";
import { getLabOrderInternalStatusDisplay } from "@/health-gorilla/utils/labs";
import { usDateFormat } from "@/utils/datetime";
import { getDisplayStringAsync } from "@/utils/medplum";

interface LabOrderItemSectionProps {
  label: string;
  value: string;
}

interface LabOrderItemProps {
  labOrder: ServiceRequest;
}

export default function LabOrderItem({ labOrder }: LabOrderItemProps) {
  const medplum = useMedplum();
  const [performer, setPerformer] = useState<string>("");

  useEffect(() => {
    (async () => {
      const performer = await getDisplayStringAsync(medplum, labOrder?.performer?.[0]);
      setPerformer(performer);
    })();
  }, [medplum, labOrder?.performer]);

  const title = useMemo(() => {
    return labOrder.code?.coding?.map((coding) => coding.display).join(", ") || "--";
  }, [labOrder.code?.coding]);

  const dateOrdered = useMemo(() => {
    return usDateFormat(labOrder.authoredOn) || usDateFormat(labOrder.meta?.lastUpdated);
  }, [labOrder.authoredOn, labOrder.meta?.lastUpdated]);

  const status = useMemo(() => {
    return getLabOrderInternalStatusDisplay(labOrder);
  }, [labOrder]);

  const notes = useMemo(() => {
    return labOrder.note?.[0]?.text || "";
  }, [labOrder.note]);

  return (
    <Accordion.Item
      value={labOrder.id!}
      style={{
        borderRadius: "var(--mantine-radius-md)",
        border: "1px solid var(--mantine-color-cream-4)",
      }}
    >
      <Accordion.Control p="md" style={{ borderRadius: "var(--mantine-radius-md)" }}>
        <Group align="center" justify="space-between">
          <Group gap="xs">
            <IconTestPipe size={24} color="var(--mantine-color-gray-6)" />
            <TextWrapper c="charcoal.0" variant="bold">
              {title}
            </TextWrapper>
          </Group>
        </Group>
      </Accordion.Control>
      <Accordion.Panel>
        <Stack gap="sm">
          <LabOrderItemSection value={dateOrdered} label="Date Ordered" />
          <LabOrderItemSection label="Lab Name" value={performer} />
          <LabOrderItemSection label="Status" value={status} />
          {notes && <LabOrderItemSection label="Notes" value={notes} />}
        </Stack>
      </Accordion.Panel>
    </Accordion.Item>
  );
}

function LabOrderItemSection({ label, value }: LabOrderItemSectionProps) {
  return (
    <Group
      p="md"
      bg="gray.0"
      justify="space-between"
      style={{ borderRadius: "var(--mantine-radius-sm)" }}
    >
      <TextWrapper c="dark.2" variant="bold">
        {label}
      </TextWrapper>
      <TextWrapper c="dark.5" variant="bold">
        {value}
      </TextWrapper>
    </Group>
  );
}
